import { useCallback, useEffect, useMemo, useState } from "react";
import {
  loadManagerAdministrationSnapshot,
  loadMemberPermissionMatrix,
  memberRoleLabel,
  type ManagerAdministrationSnapshot,
  type MemberPermissionRow,
} from "../admin/managerAdministration";
import { supabase } from "../auth/supabaseClient";
import {
  buildDutyAssignmentOperation,
  buildMembershipUpdateOperation,
  buildPermissionOverrideRemoveOperation,
  buildPermissionOverrideSetOperation,
} from "../operations/managerAdministrationOperations";
import { getOrCreateDeviceId } from "../sync/deviceIdentity";
import { persistAndSyncOperation } from "../sync/operationRuntime";
import type { OfflineOperation, OutboxSummary } from "../sync/types";
import { ConfirmationSheet } from "./ConfirmationSheet";
import { ManagerInvitationPanel } from "./ManagerInvitationPanel";

interface ManagerAdministrationPanelProps {
  farmId: string;
  userId: string;
  mode?: "workers" | "permissions" | "all";
  onSyncSummary?: (summary: OutboxSummary) => void;
}

function syncMessage(status: string): string {
  if (status === "synced") return "تغییر ثبت و همگام شد.";
  if (status === "pending") return "تغییر در صف آفلاین ثبت شد.";
  if (status === "retryable_error") return "تغییر محلی حفظ شد و دوباره ارسال می‌شود.";
  if (status === "terminal_conflict") return "تغییر ثبت محلی شد اما نیاز به بررسی تعارض دارد.";
  return "تغییر ثبت شد.";
}

function effectiveLabel(permission: MemberPermissionRow): string {
  if (permission.effectiveSource === "user_override") {
    return permission.effectiveAllowed ? "اجازه اختصاصی" : "عدم اجازه اختصاصی";
  }
  return permission.roleAllowed ? "مجاز از نقش" : "غیرمجاز از نقش";
}

export function ManagerAdministrationPanel({
  farmId,
  userId,
  mode = "all",
  onSyncSummary,
}: ManagerAdministrationPanelProps) {
  const showWorkers = mode !== "permissions";
  const showPermissions = mode !== "workers";
  const [snapshot, setSnapshot] = useState<ManagerAdministrationSnapshot>();
  const [selectedUserId, setSelectedUserId] = useState("");
  const [permissions, setPermissions] = useState<MemberPermissionRow[]>([]);
  const [membershipRole, setMembershipRole] = useState<"owner" | "manager" | "operator">(
    "operator",
  );
  const [membershipActive, setMembershipActive] = useState(true);
  const [dutyWorkerId, setDutyWorkerId] = useState("");
  const [pending, setPending] = useState<OfflineOperation>();
  const [confirmationParts, setConfirmationParts] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();

  const selectedMember = useMemo(
    () => snapshot?.members.find((member) => member.userId === selectedUserId),
    [selectedUserId, snapshot],
  );

  const activeOperators = useMemo(
    () =>
      snapshot?.members.filter(
        (member) => member.role === "operator" && member.membershipActive && member.profileActive,
      ) ?? [],
    [snapshot],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(undefined);

    try {
      const next = await loadManagerAdministrationSnapshot(supabase, farmId);
      setSnapshot(next);

      const selected =
        next.members.find((member) => member.userId === selectedUserId) ??
        next.members.find((member) => member.role !== "owner") ??
        next.members[0];

      const nextSelectedUserId = selected?.userId ?? "";
      setSelectedUserId(nextSelectedUserId);
      setMembershipRole(selected?.role ?? "operator");
      setMembershipActive(selected?.membershipActive ?? true);
      setDutyWorkerId(next.members.find((member) => member.isDutyWorker)?.userId ?? "");

      if (showPermissions && nextSelectedUserId) {
        setPermissions(await loadMemberPermissionMatrix(supabase, farmId, nextSelectedUserId));
      } else {
        setPermissions([]);
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "بارگذاری مدیریت کاربران انجام نشد.");
    } finally {
      setLoading(false);
    }
  }, [farmId, selectedUserId, showPermissions]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!selectedMember) return;
    setMembershipRole(selectedMember.role);
    setMembershipActive(selectedMember.membershipActive);
  }, [selectedMember]);

  useEffect(() => {
    if (!showPermissions || !selectedUserId) {
      setPermissions([]);
      return;
    }

    void loadMemberPermissionMatrix(supabase, farmId, selectedUserId)
      .then(setPermissions)
      .catch((caught: unknown) => {
        setError(caught instanceof Error ? caught.message : "بارگذاری دسترسی‌ها انجام نشد.");
      });
  }, [farmId, selectedUserId, showPermissions]);

  function context() {
    return {
      actorId: userId,
      farmId,
      deviceId: getOrCreateDeviceId(),
      performedAt: new Date().toISOString(),
    };
  }

  function prepare(operation: OfflineOperation, parts: string[]) {
    setMessage(undefined);
    setError(undefined);
    setPending(operation);
    setConfirmationParts(parts);
  }

  function prepareMembershipUpdate() {
    if (!selectedMember) return;

    try {
      prepare(
        buildMembershipUpdateOperation(context(), {
          targetUserId: selectedMember.userId,
          role: membershipRole,
          isActive: membershipActive,
        }),
        [
          "تغییر عضویت کاربر",
          selectedMember.fullName,
          `نقش: ${memberRoleLabel(membershipRole)}`,
          membershipActive ? "عضویت فعال" : "عضویت غیرفعال",
        ],
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "تغییر عضویت معتبر نیست.");
    }
  }

  function preparePermission(permission: MemberPermissionRow, mode: "allow" | "deny" | "reset") {
    if (!selectedMember) return;

    try {
      if (mode === "reset") {
        prepare(
          buildPermissionOverrideRemoveOperation(context(), {
            targetUserId: selectedMember.userId,
            permissionKey: permission.permissionKey,
          }),
          ["حذف دسترسی اختصاصی", selectedMember.fullName, permission.label, "بازگشت به پیش‌فرض نقش"],
        );
        return;
      }

      const decision = mode === "allow";
      prepare(
        buildPermissionOverrideSetOperation(context(), {
          targetUserId: selectedMember.userId,
          permissionKey: permission.permissionKey,
          decision,
        }),
        [
          "تغییر دسترسی اختصاصی",
          selectedMember.fullName,
          permission.label,
          decision ? "اجازه" : "عدم اجازه",
        ],
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "تغییر دسترسی معتبر نیست.");
    }
  }

  function prepareDutyAssignment() {
    const worker = activeOperators.find((member) => member.userId === dutyWorkerId);

    try {
      prepare(
        buildDutyAssignmentOperation(context(), {
          workerUserId: dutyWorkerId || null,
        }),
        ["تعیین کارگر شیفت", worker ? worker.fullName : "بدون کارگر شیفت"],
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "کارگر شیفت معتبر نیست.");
    }
  }

  async function confirm() {
    if (!pending) return;

    try {
      setBusy(true);
      setError(undefined);

      const result = await persistAndSyncOperation(
        supabase,
        pending,
        typeof navigator === "undefined" ? true : navigator.onLine !== false,
      );
      onSyncSummary?.(result.summary);
      setMessage(syncMessage(result.operation.status));
      setPending(undefined);

      if (result.operation.status === "synced") {
        await refresh();
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "ثبت تغییر مدیریتی انجام نشد.");
    } finally {
      setBusy(false);
    }
  }

  if (loading && !snapshot) {
    return (
      <section className="section-card">
        <p>در حال بارگذاری مدیریت کاربران…</p>
      </section>
    );
  }

  return (
    <section className="section-card" aria-labelledby="manager-admin-title">
      <div className="section-title-row">
        <div>
          <p className="section-kicker">M4A</p>
          <h2 id="manager-admin-title">
            {mode === "workers"
              ? "مدیریت کارگران"
              : mode === "permissions"
                ? "دسترسی‌ها و نقش‌ها"
                : "کاربران، دسترسی و شیفت"}
          </h2>
        </div>
        <button
          type="button"
          className="text-button"
          disabled={busy}
          onClick={() => void refresh()}
        >
          بروزرسانی
        </button>
      </div>

      <p className="section-note">
        {mode === "workers"
          ? "دعوت کارگر، عضویت و تعیین کارگر فعال شیفت از مسیر canonical ثبت می‌شود."
          : mode === "permissions"
            ? "این بخش فقط مدیریت نقش و دسترسی اختصاصی کاربران سالن را انجام می‌دهد."
            : "تغییرات عضویت، دسترسی و شیفت از مسیر canonical و صف همگام‌سازی ثبت می‌شوند."}
      </p>

      {showWorkers && snapshot ? (
        <ManagerInvitationPanel
          farmId={farmId}
          canManageUsers={snapshot.capabilities.canManageUsers}
          onCompleted={() => void refresh()}
        />
      ) : null}

      {snapshot?.members.length ? (
        <>
          <div className="operation-field-grid">
            <label>
              کاربر
              <select
                value={selectedUserId}
                onChange={(event) => setSelectedUserId(event.target.value)}
              >
                {snapshot.members.map((member) => (
                  <option value={member.userId} key={member.userId}>
                    {member.fullName} · {memberRoleLabel(member.role)}
                    {member.membershipActive ? "" : " · غیرفعال"}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {showWorkers && selectedMember ? (
            <article className="manager-card">
              <div className="section-title-row">
                <div>
                  <p className="section-kicker">عضویت</p>
                  <h3>{selectedMember.fullName}</h3>
                </div>
                <span className="count-pill">{memberRoleLabel(selectedMember.role)}</span>
              </div>

              <p className="section-note">
                حساب: {selectedMember.profileActive ? "فعال" : "غیرفعال"} · عضویت سالن:{" "}
                {selectedMember.membershipActive ? "فعال" : "غیرفعال"}
                {selectedMember.isDutyWorker ? " · کارگر شیفت" : ""}
              </p>

              {snapshot.capabilities.canManageUsers ? (
                <>
                  <div className="operation-field-grid">
                    <label>
                      نقش
                      <select
                        value={membershipRole}
                        onChange={(event) =>
                          setMembershipRole(event.target.value as "owner" | "manager" | "operator")
                        }
                      >
                        <option value="owner">مالک</option>
                        <option value="manager">مدیر</option>
                        <option value="operator">کارگر</option>
                      </select>
                    </label>

                    <label>
                      وضعیت عضویت
                      <select
                        value={membershipActive ? "active" : "inactive"}
                        onChange={(event) => setMembershipActive(event.target.value === "active")}
                      >
                        <option value="active">فعال</option>
                        <option value="inactive">غیرفعال</option>
                      </select>
                    </label>
                  </div>

                  <button
                    type="button"
                    className="secondary-button"
                    disabled={busy}
                    onClick={prepareMembershipUpdate}
                  >
                    بررسی تغییر عضویت
                  </button>
                </>
              ) : (
                <p className="section-note">
                  تغییر نقش و عضویت فقط با دسترسی مدیریت کاربران مجاز است.
                </p>
              )}
            </article>
          ) : null}

          {showPermissions ? (
            <article className="manager-card">
              <div className="section-title-row">
                <div>
                  <p className="section-kicker">دسترسی‌ها</p>
                  <h3>پیش‌فرض نقش و Override کاربر</h3>
                </div>
              </div>

              <div className="work-list">
                {permissions.map((permission) => (
                  <div className="work-item pending" key={permission.permissionKey}>
                    <span className="work-main">
                      <strong>{permission.label}</strong>
                      <span>
                        {effectiveLabel(permission)}
                        {permission.managerConfigurable ? "" : " · محافظت‌شده"}
                      </span>
                    </span>

                    {snapshot.capabilities.canManagePermissions &&
                    selectedMember?.role !== "owner" &&
                    permission.managerConfigurable ? (
                      <span className="confirm-actions">
                        <button
                          type="button"
                          className="text-button"
                          onClick={() => preparePermission(permission, "allow")}
                        >
                          اجازه
                        </button>
                        <button
                          type="button"
                          className="text-button"
                          onClick={() => preparePermission(permission, "deny")}
                        >
                          عدم اجازه
                        </button>
                        <button
                          type="button"
                          className="text-button"
                          disabled={permission.overrideDecision === null}
                          onClick={() => preparePermission(permission, "reset")}
                        >
                          پیش‌فرض نقش
                        </button>
                      </span>
                    ) : null}
                  </div>
                ))}
              </div>
            </article>
          ) : null}

          {showWorkers ? (
            <article className="manager-card">
              <div className="section-title-row">
                <div>
                  <p className="section-kicker">شیفت</p>
                  <h3>کارگر فعال شیفت</h3>
                </div>
              </div>

              {snapshot.capabilities.canManageDuty ? (
                <>
                  <div className="operation-field-grid">
                    <label>
                      کارگر شیفت
                      <select
                        value={dutyWorkerId}
                        onChange={(event) => setDutyWorkerId(event.target.value)}
                      >
                        <option value="">بدون کارگر شیفت</option>
                        {activeOperators.map((member) => (
                          <option value={member.userId} key={member.userId}>
                            {member.fullName}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  <button
                    type="button"
                    className="secondary-button"
                    disabled={busy}
                    onClick={prepareDutyAssignment}
                  >
                    بررسی تغییر شیفت
                  </button>
                </>
              ) : (
                <p className="section-note">دسترسی مدیریت کارگر شیفت برای این حساب فعال نیست.</p>
              )}
            </article>
          ) : null}
        </>
      ) : (
        <p className="empty-state">عضوی برای این سالن نمایش داده نشد.</p>
      )}

      {message ? <p className="operation-success">{message}</p> : null}
      {error ? <p className="settings-warning">{error}</p> : null}

      {pending ? (
        <ConfirmationSheet
          title="تأیید تغییر مدیریتی"
          parts={confirmationParts}
          speechEnabled
          fastConfirmEnabled
          onCancel={() => setPending(undefined)}
          onConfirm={() => void confirm()}
        />
      ) : null}
    </section>
  );
}
